export type BpsOberflaechenTyp = 'beschichtet' | 'geschweisst';
export type BpsRolleMessArt = 'durchmesser' | 'umfang';
export type BpsMessBereich = 'klein' | 'groß' | 'kombiniert' | undefined;
export type BpsMessBereichReichweite = number | undefined;
export type BpsPositionRolle = 'links' | 'rechts';
export type BpsMesswertAnzeige = 'analog' | 'digital' | undefined;

export enum BpsArt {
  Rolle = 'rolle',
  Platte = 'platte'
}

export interface IBpsReferenzkraftMesswerte {
  mittelwerte: {
    links: number;
    rechts: number;
  };
  messbereich: string;
  messbereiche: {
    klein?: number;
    gross?: number;
  };
  objekt: string;
}

interface IBpsReferenzkraftMesswertePunkt {
  anzeige: number;
  fehlergrenze: number;
}

export interface IBpsReferenzkraftRollensatzMesswert {
  bps: number | null;
  sensor: number | null;
  referenzwert: number | null;
  messwertabweichung: number | null;
}

interface IBpsReferenzkraftBeurteilungRequestMesspunkt {
  messwerte: (number | null)[];
  fehlergrenze: number | null;
  messunsicherheit: number | null;
  abweichung: number | null;
}
export interface IBpsReferenzkraftBeurteilungRequest {
  mittelwerte: {
    links?: number | null;
    rechts?: number | null;
  };
  position: string;
  messbereich: string | undefined;
  messbereiche: {
    klein?: number;
    gross?: number;
  };
  messwerte: IBpsReferenzkraftBeurteilungRequestMesspunkt[];
  objekt: string;
  kmg: string;
}

export interface IBpsReferenzkraftBeurteilungPunkt {
  risiko: number | null;
  bewertung: boolean | null;
}

export interface IBpsReferenzkraftGesamtMesspunkt {
  kraftstufe: number;
  links: number;
  rechts: number;
  absolut: number;
  relativ: number | null;
  bewertung: boolean;
}

/** Interfaces für Prüfortparameter */
export interface IBpsReferenzkraftPruefortParameterKomponente {
  wert: number | null;
  min: number;
  max: number;
}

export interface IBpsReferenzkraftPruefortParameter {
  lufttemperatur: IBpsReferenzkraftPruefortParameterKomponente;
  luftfeuchtigkeit: IBpsReferenzkraftPruefortParameterKomponente;
}

/** Interfaces mit allen Daten zu einer Rollensatz-Vorbelastung */
export interface IBpsReferenzkraftRollensatzVorbelastungMesspunkt {
  anzeige: number | null;
  normal: number | null;
}

export interface IBpsReferenzkraftRollensatzVorbelastung {
  messpunkte: IBpsReferenzkraftRollensatzVorbelastungMesspunkt[];
}

/** Interface mit allen Daten zu einem Rollensatz-Messpunkt */
export interface IBpsReferenzkraftRollensatzMesspunkt {
  anzeige: number;
  messwerte: Array<IBpsReferenzkraftRollensatzMesswert>;
  mittelwert: number | undefined;
  mittelwertabweichung: number | null;
  messunsicherheit: number | null;
  beurteilung: boolean | null;
}

/** Interface mit allen Daten zu einem Rollensatz */
export interface IBpsReferenzkraftRollensatz {
  position: BpsPositionRolle;
  messpunkte: IBpsReferenzkraftRollensatzMesspunkt[];
  vorbelastung: IBpsReferenzkraftRollensatzVorbelastung;
}

/** Rollendurchmesser */

export interface IBpsReferenzkraftRollendurchmesser {
  messwerte: Array<Array<number | null>>;
  mittelwert: {
    links: number | null;
    rechts: number | null;
  };
  faktor: {
    links: number | null;
    rechts: number | null;
  };
  grunddurchmesser: number | undefined;
}

/** Wägezelle */

export interface IBpsReferenzkraftWaegezelle {
  messwerte: Array<number | undefined>;
  sollwert: number;
  toleranz: number;
}


/** Rolllendrehzahl und Abschaltschlupf via ASA-Livestream */
export interface IBpsReferenzkraftDrehzahlSchlupfRollensatz {
  geschwindigkeit: Array<number | undefined>;
  schlupf: number | null;
}

export interface IBpsReferenzkraftDrehzahlSchlupf {
  links: IBpsReferenzkraftDrehzahlSchlupfRollensatz;
  rechts: IBpsReferenzkraftDrehzahlSchlupfRollensatz;
  mingeschwindigkeit: number;
  maxschlupf: number;
}

export interface IBpsReferenzKraftGesamtErgebnis extends Array<IBpsReferenzkraftGesamtMesspunkt> {}

/** Interface mit allen Daten zur Messwerteingabe- und Berechnung für die Beurteilung der BPS Rolle */
export interface IBpsReferenzkraftRolleMessung {
  version: number | null;
  richtlinie: string;
  rollendurchmesser: IBpsReferenzkraftRollendurchmesser;
  oberflaeche: BpsOberflaechenTyp;
  messart: BpsRolleMessArt;
  messbereich: BpsMessBereich;
  messbereichKlein: BpsMessBereichReichweite;
  messbereichGross: BpsMessBereichReichweite;
  anzeige: BpsMesswertAnzeige;
  justageInfo: string;
  rollensatz: {
    links: IBpsReferenzkraftRollensatz;
    rechts: IBpsReferenzkraftRollensatz;
  };
  rollensatzJustage: {
    links: IBpsReferenzkraftRollensatz;
    rechts: IBpsReferenzkraftRollensatz;
    gesamtErgebnis: IBpsReferenzKraftGesamtErgebnis;
  };
  pruefortparameter: {
    beginn: IBpsReferenzkraftPruefortParameter;
    ende: IBpsReferenzkraftPruefortParameter;
  };
  gesamtErgebnis: IBpsReferenzKraftGesamtErgebnis;
  waegezelle: IBpsReferenzkraftWaegezelle;
  drehzahlschlupf: IBpsReferenzkraftDrehzahlSchlupf;
  kmg: string;
  objekt: string;
}

/** Interfaces für Back-End Kommunikation */
export interface IBpsReferenzkraftBeurteilungResponse {
  links: Array<IBpsReferenzkraftBeurteilungPunkt>;
  rechts: Array<IBpsReferenzkraftBeurteilungPunkt>;
  gesamt: Array<IBpsReferenzkraftGesamtMesspunkt>;
  justage?: {
    links: Array<IBpsReferenzkraftBeurteilungPunkt>;
    rechts: Array<IBpsReferenzkraftBeurteilungPunkt>;
    gesamt: Array<IBpsReferenzkraftGesamtMesspunkt>;
  };
}
export interface IBpsReferenzkraftMesswerteResponse extends Array<IBpsReferenzkraftMesswertePunkt> { }



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/models/ApiTypesBps2021.ts